import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { type ReactElement, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/i18n';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';

import { HistoryRow } from './HistoryRow';
import { type HistoryDay, groupByDay } from './history-model';
import { useHistory } from './use-history';

/** Sizes in px drawn on mockup Historique (`docs/06-MiniApp/Maquettes/Historique.html`). */
const MOCKUP = {
  /** Day title ("TODAY"): 12px, semibold, upper case, spaced. */
  daySize: 12,
  /** Corner of a day's group of rows. */
  groupRadius: '14px',
  /** Hint under the list. */
  hintSize: 13,
  /** Height of a row skeleton while the first page loads. */
  rowSkeletonHeight: 72,
} as const;

/**
 * Screen E9 · History, the second tab of Positions (specs/M6-history/spec.md; US-16; mockup
 * Historique). Closed positions grouped by local day, newest first, each with its close reason and
 * result; tapping one opens its M5 timeline (I10). "Show more" loads the next page (R8). Fetched
 * when the tab opens and when the app becomes visible again, never on a timer (R9). No total or
 * statistic (R11, FXM-04 §8).
 */
export function HistoryTab(): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const history = useHistory();
  const { positions, receivedAt } = history;
  const days = useMemo(
    () =>
      positions === undefined || receivedAt === undefined
        ? undefined
        : groupByDay(positions, { now: receivedAt, language }),
    [positions, receivedAt, language],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {history.error !== undefined && (
        <Failure error={history.error} onRetry={history.refetch} retrying={history.retrying} />
      )}
      {days === undefined ? (
        history.error === undefined && <Loading />
      ) : (
        <>
          {days.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('history.empty')}
              </Typography>
            </Paper>
          ) : (
            days.map((day) => <Day key={day.key} day={day} />)
          )}
          {history.loadMoreError !== undefined && <Failure error={history.loadMoreError} />}
          {/* A cursor means more rows, even after an empty page (C1: the cursor decides, R8). */}
          {history.nextCursor !== null && (
            <Button
              variant="outlined"
              onClick={history.loadMore}
              // Busy also while a refused cursor is recovered from (M6 §8): a tap would resend it.
              loading={history.loadingMore || history.recovering}
              sx={{ alignSelf: 'center', minHeight: 44 }}
            >
              {t('history.showMore')}
            </Button>
          )}
          {days.length > 0 && (
            <Typography
              component="p"
              sx={{ mx: 0.5, fontSize: MOCKUP.hintSize, color: 'text.secondary', lineHeight: 1.5 }}
            >
              {t('history.hint')}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

/** One day: its title ("Today", "Yesterday" or the date) and its rows in one group (mockup). */
function Day({ day }: { readonly day: HistoryDay }): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  const title =
    day.kind === 'today'
      ? t('history.today')
      : day.kind === 'yesterday'
        ? t('history.yesterday')
        : day.dateLabel;
  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box
        component="h2"
        id={titleId}
        sx={{
          m: 0,
          px: 0.5,
          pt: 0.5,
          fontSize: MOCKUP.daySize,
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {title}
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: MOCKUP.groupRadius, overflow: 'hidden' }}>
        {day.positions.map((position) => (
          <HistoryRow key={position.id} position={position} />
        ))}
      </Paper>
    </Box>
  );
}

/** Skeleton rows while the first page loads (spec §4 "Loading"), announced to screen readers. */
function Loading(): ReactElement {
  const { t } = useTranslation();
  return (
    <Box role="status" aria-label={t('history.loading')} sx={{ display: 'grid', gap: 1 }}>
      <Skeleton variant="rounded" height={MOCKUP.rowSkeletonHeight} />
      <Skeleton variant="rounded" height={MOCKUP.rowSkeletonHeight} />
    </Box>
  );
}

/**
 * A failure: only the translated reason, never a raw code or message (I9, I12). With `onRetry`, a
 * Retry button (first page); without, the "Show more" button below is the retry.
 */
function Failure({
  error,
  onRetry,
  retrying = false,
}: {
  readonly error: ApiError;
  readonly onRetry?: () => void;
  /** The Retry button shows it is busy until the answer arrives (as E8, M5 §4 "Error"). */
  readonly retrying?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const message = reasonMessage(error.reasonCode, error.params);
  return (
    <Alert
      severity="error"
      sx={(theme) => ({ bgcolor: theme.fx.dangerBg, color: theme.fx.text })}
      action={
        onRetry === undefined ? undefined : (
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            loading={retrying}
            sx={{ minHeight: 36 }}
          >
            {t('history.retry')}
          </Button>
        )
      }
    >
      {t(message.key, message.values)}
    </Alert>
  );
}
